"use client";

import { motion } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";

interface AnimatedCheckmarkProps {
  variant?: "default" | "inverted";
  size?: number | { mobile: number; desktop: number };
}

export const AnimatedCheckmark = ({
  variant = "default",
  size,
}: AnimatedCheckmarkProps) => {
  const isMobile = useIsMobile();

  // Always use green circle with white check on mobile
  const circleColor = isMobile
    ? "rgb(22 163 74)"
    : variant === "inverted"
      ? "white"
      : "rgb(22 163 74)";
  const checkColor = isMobile
    ? "white"
    : variant === "inverted"
      ? "rgb(22 163 74)"
      : "white";

  // Base size and scaling
  const baseSize = 28; // Original desktop size

  // Determine the rendered size based on props and device
  const defaultMobileSize = 200;
  const defaultDesktopSize = 45;

  let renderedSize: number;

  if (size === undefined) {
    // Use default sizes if no size prop provided
    renderedSize = isMobile ? defaultMobileSize : defaultDesktopSize;
  } else if (typeof size === "number") {
    // Use the same size for both mobile and desktop
    renderedSize = size;
  } else {
    // Use different sizes for mobile and desktop
    renderedSize = isMobile ? size.mobile : size.desktop;
  }

  // Adjust the check path to be smaller and slightly higher
  const checkPath = `M ${baseSize * 0.35} ${baseSize * 0.52} L ${
    baseSize * 0.45
  } ${baseSize * 0.62} L ${baseSize * 0.65} ${baseSize * 0.37}`;

  return (
    <motion.svg
      width={renderedSize}
      height={renderedSize}
      viewBox={`0 0 ${baseSize} ${baseSize}`} // Keep viewBox constant
      initial='hidden'
      animate='visible'
      className={variant === "inverted" ? "text-white" : "text-green-600"}
    >
      <motion.circle
        cx={baseSize / 2}
        cy={baseSize / 2}
        r={baseSize / 2 - 4}
        fill={circleColor}
        variants={{
          hidden: {
            scale: 0,
            opacity: 0,
          },
          visible: {
            scale: 1,
            opacity: 1,
            transition: {
              duration: 0.3,
              ease: [0.34, 1.56, 0.64, 1],
            },
          },
        }}
      />
      <motion.path
        fill='none'
        stroke={checkColor}
        strokeWidth={3} // Keep stroke width constant in viewBox space
        strokeLinecap='round'
        strokeLinejoin='round'
        d={checkPath}
        variants={{
          hidden: {
            pathLength: 0,
            opacity: 0,
          },
          visible: {
            pathLength: 1,
            opacity: 1,
            transition: {
              delay: 0.1,
              duration: 0.4,
              ease: "easeOut",
              opacity: { duration: 0.1 },
            },
          },
        }}
      />
    </motion.svg>
  );
};
